import { Link } from "react-router-dom";
import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import PageHero from "@/components/PageHero";
import { COMPANY } from "@/lib/brand";
import { usePageMeta } from "@/lib/seo";
import { Phone, Mail, MapPin, ArrowRight, Clock } from "lucide-react";

export default function Contact() {
  usePageMeta(
    "Contact Us | House Clearance Peterborough | Waites Clearances",
    "Contact Waites Clearances, the family run house clearance team based in Peterborough. Call 07761 748698 or email us for a free quote — serving Peterborough and surrounding areas."
  );
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="page-contact">
      <Header />

      <PageHero
        eyebrow="Contact"
        title="Get in touch with Peterborough's trusted clearance team."
        description="Prefer to talk it through before booking? Give our Peterborough team a call or drop an email — we'll come back to you the same day. For the fastest response, use the booking form and we'll confirm your date by email."
      />

      <section className="py-20 sm:py-24" data-testid="contact-details">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Details card */}
          <div className="lg:col-span-3 rounded-3xl border border-slate-200 bg-white p-8 sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">
              How to reach us
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              {COMPANY.tradingName}
            </h2>

            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4" data-testid="contact-phone">
                <div className="h-11 w-11 rounded-2xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-[#FF7F00]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Phone</p>
                  <a
                    href={`tel:${COMPANY.phoneTel}`}
                    className="mt-1 block font-display text-lg font-bold text-slate-900 hover:text-[#FF7F00]"
                  >
                    {COMPANY.phoneDisplay}
                  </a>
                  <p className="text-sm text-slate-500 mt-1">Mon–Sat, 8am–6pm</p>
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-email">
                <div className="h-11 w-11 rounded-2xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-[#FF7F00]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Email</p>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="mt-1 block font-display text-lg font-bold text-slate-900 hover:text-[#FF7F00] break-all"
                  >
                    {COMPANY.email}
                  </a>
                  <p className="text-sm text-slate-500 mt-1">We reply the same day</p>
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-address">
                <div className="h-11 w-11 rounded-2xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-[#FF7F00]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Address</p>
                  <p className="mt-1 font-display text-lg font-bold text-slate-900 leading-snug">
                    {COMPANY.address.line1}
                    <br />
                    {COMPANY.address.line2}
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-hours">
                <div className="h-11 w-11 rounded-2xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center shrink-0">
                  <Clock className="h-5 w-5 text-[#FF7F00]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Collections</p>
                  <p className="mt-1 font-display text-lg font-bold text-slate-900">
                    Choose your preferred date
                  </p>
                  <p className="text-sm text-slate-500 mt-1">Same-week availability across Peterborough and surrounding areas</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Book form card */}
          <div className="lg:col-span-2">
            <div className="sticky top-24 rounded-3xl bg-[#0F172A] p-8 sm:p-10 text-white overflow-hidden">
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF9933]">
                  Fastest way
                </p>
                <h3 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight">
                  Book online in under 3 minutes.
                </h3>
                <p className="mt-3 text-slate-300 leading-relaxed">
                  Answer five short steps with your address, the items you'd like collected,
                  a couple of room photos and your preferred date — we'll email you back to confirm.
                </p>

                <ol className="mt-6 space-y-2 text-sm text-slate-300">
                  <li>1. Address</li>
                  <li>2. Items</li>
                  <li>3. Room photos</li>
                  <li>4. Preferred date</li>
                  <li>5. Your details</li>
                </ol>

                <Link
                  to="/book"
                  data-testid="contact-cta-book"
                  className="mt-8 inline-flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-500/20 transition-colors"
                >
                  Start the booking <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="mt-3 text-center text-xs text-white/60">Free quote • No obligation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
